import { serialize as serializeToFormData } from "object-to-formdata";
import { Change, ExpandedImages } from "types";
import Compressor from "compressorjs";

type UrlParams = Record<string, string>;

const csrfToken =
  document.querySelector("[name='csrf-token']")?.getAttribute("content") || "";

if (!csrfToken) {
  throw new Error(`no CSRF token found`);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function fetchWithBody(method: string, url: string, body: any) {
  const requestOptions: RequestInit = {
    method: method,
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken,
    },
    body: JSON.stringify(body),
  };

  const response = await fetch(url, requestOptions);
  return response.json();
}

async function fetchWithUrlParams(
  method: string,
  url: string,
  params: UrlParams,
) {
  const requestOptions: RequestInit = {
    method: method,
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken,
    },
  };

  const parameters = new URLSearchParams(params);
  url = url + (parameters.toString() ? `?${parameters.toString()}` : "");
  const response = await fetch(url, requestOptions);
  return response.json();
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function formPostRequest(url: string, body: any) {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: {
      "X-CSRF-Token": csrfToken,
    },
    body: serializeToFormData(body),
  };

  const response = await fetch(url, requestOptions);
  return response.json();
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function postRequest(url: string, body: any) {
  return fetchWithBody("POST", url, body);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function putRequest(url: string, body: any) {
  return fetchWithBody("PUT", url, body);
}

export async function deleteRequest(url: string, params: UrlParams = {}) {
  return fetchWithUrlParams("DELETE", url, params);
}

export async function getRequest(url: string, params: UrlParams = {}) {
  return fetchWithUrlParams("GET", url, params);
}

export function currentTimeUnixTimestamp() {
  return Math.floor(Date.now() / 1000);
}

export function unixTimestampToDate(unixTimestamp: number) {
  return new Date(unixTimestamp * 1000);
}

export function onPageLoad(callback: () => void) {
  if (document.readyState === "loading") {
    // Loading hasn't finished yet
    document.addEventListener("DOMContentLoaded", callback);
  } else {
    // `DOMContentLoaded` has already fired
    callback();
  }
}

export function onChecklistPageLoad(callback: () => void) {
  onPageLoad(() => {
    const notChecklistShowPage = !document.getElementById(
      "checklist-show-page",
    );
    if (notChecklistShowPage) {
      return;
    }
    callback();
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function postFormData(url: string, body: any) {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: { "X-CSRF-Token": csrfToken },
    body: body,
  };

  const response = await fetch(url, requestOptions);
  return response.json();
}

// below constants duplicate constants defined in service_worker.ts
// had to do that because I couldn't figure out how to use import/export statements in service workers
// and keep both the browser and typescript compiler/linter happy
const serviceWorkerCacheName = "wiederholServiceWorkerCacheV1";
const browserImageCachePrefix = "browser-image-cache";

export async function cacheImage(image: File): Promise<string> {
  const cache = await caches.open(serviceWorkerCacheName);
  const url = `/${browserImageCachePrefix}/${crypto.randomUUID()}-${encodeURIComponent(image.name)}`;
  const response = new Response(image, {
    headers: { "Content-Type": image.type || "application/octet-stream" },
  });

  await cache.put(url, response);

  return url;
}

export async function imageUploadParams(change: Change) {
  if (
    (change.name != "updateItem" && change.name != "createItem") ||
    !Array.isArray(change.payload.images) ||
    !(change.payload.images.length > 0)
  ) {
    return [];
  }
  const result: ExpandedImages = {};
  await Promise.all(
    change.payload.images.map(async ({ url }) => {
      const image = await getCachedImage(url);
      if (image) {
        result[url] = image;
      }
    }),
  );
  return result;
}

async function getCachedImage(url: string): Promise<File | null> {
  const cache = await caches.open(serviceWorkerCacheName);
  const response = await cache.match(url);

  if (!response) {
    return null;
  }

  const blob = await response.blob();

  // Derive filename from the URL (strip "/cached/")
  const filename = decodeURIComponent(url.split("/").pop() || "unknown");

  return new File([blob], filename, { type: blob.type });
}

// https://github.com/fengyuanchen/compressorjs
export async function compressImage(image: File): Promise<File> {
  return new Promise((resolve, reject) => {
    new Compressor(image, {
      quality: 0.6,
      convertTypes:
        "image/png,image/webp,image/apng,image/avif,image/gif,image/bmp",
      convertSize: 0,
      success(result) {
        resolve(result as File);
      },
      error(err) {
        reject(err);
      },
    });
  });
}
